import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Mail, Share2 } from "lucide-react";
import stories from "@/content/pribehy.json";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";

const MAILTO =
  "mailto:danielpekar@icloud.com?subject=" + encodeURIComponent("Můj příběh — Nebuď šmejd");

export const shareUrl = (story) =>
  "https://wa.me/?text=" +
  encodeURIComponent(`${story.title} — opravdový příběh z rubriky Nebuď šmejd: https://pekardaniel.cz/pribehy/${story.slug}`);

export default function Pribehy() {
  useEffect(() => {
    document.title = "Nebuď šmejd — opravdové příběhy klientů | Daniel Pekař";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", "Skutečné příběhy klientů o tom, co se stane, když poradce nedělá svou práci pořádně. Anonymně a se souhlasem.");
    return () => { document.title = "Daniel Pekař – finanční poradce Ústí nad Labem"; };
  }, []);

  return (
    <>
      <Navbar />
      <main className="pt-32 pb-24 md:pt-40 md:pb-32">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <p className="flex items-center gap-4 text-xs uppercase tracking-[0.25em] text-[#737373] font-medium">
            <span className="w-10 h-[1px] bg-[#B69A58]" aria-hidden="true" />
            Opravdové příběhy klientů
          </p>
          <h1 className="font-display text-4xl md:text-5xl tracking-tight leading-[1.12] text-[#252525] mt-6">
            Nebuď šmejd
          </h1>
          <p className="text-base md:text-lg text-[#4A4A4A] leading-relaxed mt-8">
            Příběhy lidí, kteří se společně se mnou rozhodli svěřit svoji zkušenost dál —
            aby se nestala i vám. Všechny jsou skutečné, se souhlasem klienta
            a v anonymizované podobě.
          </p>

          <div className="mt-14 space-y-10">
            {stories.map((story) => (
              <article key={story.slug} data-testid={`pribeh-card-${story.slug}`} className="border border-[#F0F0F0] p-8 md:p-10 hover:border-[#B69A58] transition-colors duration-300">
                <p className="text-xs uppercase tracking-widest text-[#737373]">
                  {new Date(story.date).toLocaleDateString("cs-CZ", { day: "numeric", month: "long", year: "numeric" })}
                </p>
                <h2 className="font-display text-2xl md:text-3xl leading-snug text-[#252525] mt-4">
                  <Link to={`/pribehy/${story.slug}`} data-testid={`pribeh-link-${story.slug}`} className="hover:text-[#B69A58] transition-colors duration-300">
                    {story.title}
                  </Link>
                </h2>
                <p className="text-base text-[#4A4A4A] leading-relaxed mt-4">{story.excerpt}</p>
                <div className="mt-6 flex flex-wrap items-center gap-x-8 gap-y-3">
                  <Link
                    to={`/pribehy/${story.slug}`}
                    data-testid={`pribeh-cta-${story.slug}`}
                    className="group inline-flex items-center gap-2 text-xs uppercase tracking-widest font-medium text-[#252525] hover:text-[#B69A58] transition-colors duration-300"
                  >
                    Číst příběh
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.5} />
                  </Link>
                  <a
                    href={shareUrl(story)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`pribeh-share-${story.slug}`}
                    className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-medium text-[#737373] hover:text-[#252525] transition-colors duration-300"
                  >
                    <Share2 className="h-4 w-4" strokeWidth={1.5} />
                    Poslat dál přes WhatsApp
                  </a>
                </div>
              </article>
            ))}
          </div>

          <div className="mt-16 border border-[#F0F0F0] p-8 md:p-10" data-testid="pribehy-submit-cta">
            <h2 className="font-display text-2xl md:text-3xl text-[#252525]">Stalo se vám něco podobného?</h2>
            <p className="text-base text-[#4A4A4A] leading-relaxed mt-4">
              Napište mi svůj příběh e-mailem — stačí pár vět o tom, co se stalo.
              Přečtu ho, společně doladíme znění a zveřejníme ho v této rubrice.
              Anonymně, pokud budete chtít.
            </p>
            <a
              href={MAILTO}
              data-testid="pribehy-mailto-button"
              className="mt-8 inline-flex items-center gap-2 border border-[#B69A58] bg-white text-[#252525] hover:bg-[#B69A58] hover:text-white transition-colors duration-300 px-8 py-4 text-xs uppercase tracking-widest font-medium"
            >
              <Mail className="h-4 w-4 text-[#B69A58]" strokeWidth={1.5} aria-hidden="true" />
              Poslat svůj příběh
            </a>
            <p className="font-display italic text-lg text-[#252525] mt-8">— Daniel</p>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
